import React from "react"
import classnames from "classnames"
import { ReactComponent as EditIcon } from "../../images/icons/edit.svg"
import Switch from "../../components/Switch/Switch"
import Input from "../../components/Input/Input"
import EmailMfaOtpChallenge from "../../components/EmailMfaOtpChallenge/EmailMfaOtpChallenge"
import styles from "./index.module.scss"
import services from "../../services"
import { toast } from "react-toastify"
import ToastInfo from "../../components/ToastContents/ToastInfo"
import {
  isRateLimitError,
  getMfaRateLimitMessage,
  getMfaErrorMessage,
  showMfaRateLimitToast,
} from "../../helpers/mfaErrors"

const E164_PHONE = /^\+[1-9]\d{1,14}$/

const isValidE164Phone = (phone) =>
  typeof phone === "string" && E164_PHONE.test(phone.trim())

const SMS_PHONE_MISSING_MESSAGE =
  "Add a mobile phone number before enabling SMS notifications."

const SMS_PHONE_INVALID_MESSAGE =
  "Your phone number is not in the correct format. Use E.164 format (e.g. +15551234567) before enabling SMS notifications."

const NotificationsSettingsContainer = ({
  data,
  updateSettings,
  refreshSettings,
}) => {
  const [email, setEmail] = React.useState(data.email)
  const [phoneNumber, setPhoneNumber] = React.useState(data.phone_number)
  // Strict boolean: missing/undefined backend fields must default OFF (not ON)
  const [sendToEmail, setSendToEmail] = React.useState(
    data.email_notifications === true,
  )
  const [sendToMobile, setSendToMobile] = React.useState(
    data.sms_notifications === true,
  )
  const [editingPhone, setEditingPhone] = React.useState(false)
  const [mfaEnabled, setMfaEnabled] = React.useState(data.mfa_enabled === true)
  const [mfaBusy, setMfaBusy] = React.useState(false)
  const [mfaChallenge, setMfaChallenge] = React.useState(null)
  const [mfaOtp, setMfaOtp] = React.useState("")
  const [mfaError, setMfaError] = React.useState("")
  const [mfaSwitchKey, setMfaSwitchKey] = React.useState(0)
  const [notifSwitchKey, setNotifSwitchKey] = React.useState(0)
  const [smsBusy, setSmsBusy] = React.useState(false)
  const [smsMessage, setSmsMessage] = React.useState("")
  const mfaPanelRef = React.useRef(null)
  const mfaOtpInputRef = React.useRef(null)
  const mfaToggleInFlightRef = React.useRef(false)
  const mfaConfirmInFlightRef = React.useRef(false)
  const mfaConfirmGenRef = React.useRef(0)
  const mfaChallengeRef = React.useRef(null)

  // Master "Allow notifications" = either channel enabled (not a separate API field).
  const allowNotifications = sendToEmail || sendToMobile

  const remountNotifSwitches = () => setNotifSwitchKey((key) => key + 1)

  React.useEffect(() => {
    mfaChallengeRef.current = mfaChallenge
  }, [mfaChallenge])

  React.useEffect(() => {
    setEmail(data.email)
    setPhoneNumber(data.phone_number)
    setSendToEmail(data.email_notifications === true)
    setSendToMobile(data.sms_notifications === true)
    setMfaEnabled(data.mfa_enabled === true)
  }, [data])

  // When OTP panel opens, scroll it into view (esp. mobile) and focus the input
  React.useEffect(() => {
    if (!mfaChallenge) return undefined

    const timer = window.setTimeout(() => {
      const panel = mfaPanelRef.current
      if (panel && typeof panel.scrollIntoView === "function") {
        panel.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        })
      }
      if (mfaOtpInputRef.current) {
        mfaOtpInputRef.current.focus({ preventScroll: true })
      }
    }, 100)

    return () => window.clearTimeout(timer)
  }, [mfaChallenge])

  const handleToggleAllowNotifications = () => {
    if (allowNotifications) {
      // Master OFF → disable both channels independently on the API
      setSendToEmail(false)
      setSendToMobile(false)
      updateSettings({ email_notifications: false, sms_notifications: false })
        .catch(() => {
          setSendToEmail(data.email_notifications === true)
          setSendToMobile(data.sms_notifications === true)
          remountNotifSwitches()
          showToast("Failed to update notification settings")
        })
    } else {
      // Master ON → enable email channel by default (SMS stays off until user opts in)
      setSendToEmail(true)
      updateSettings({ email_notifications: true })
        .catch(() => {
          setSendToEmail(false)
          remountNotifSwitches()
          showToast("Failed to update notification settings")
        })
    }
  }

  const handleToggleSendToEmail = () => {
    const newValue = !sendToEmail
    setSendToEmail(newValue)
    updateSettings({ email_notifications: newValue })
      .catch(() => {
        setSendToEmail(!newValue)
        remountNotifSwitches()
        showToast("Failed to update email notification settings")
      })
  }

  const canEnableSms = isValidE164Phone(phoneNumber)

  const showSmsPhoneRequiredToast = () => {
    const trimmed = typeof phoneNumber === "string" ? phoneNumber.trim() : ""
    const message = trimmed
      ? SMS_PHONE_INVALID_MESSAGE
      : SMS_PHONE_MISSING_MESSAGE
    toast.error(<ToastInfo>{message}</ToastInfo>, {
      position: toast.POSITION.TOP_RIGHT,
    })
  }

  const handleToggleSendToMobile = () => {
    const newValue = !sendToMobile
    if (newValue && !canEnableSms) {
      // Switch flips optimistically — remount to keep it OFF and tell the user why.
      remountNotifSwitches()
      showSmsPhoneRequiredToast()
      return
    }
    setSendToMobile(newValue)
    updateSettings({ sms_notifications: newValue })
      .catch((error) => {
        setSendToMobile(!newValue)
        remountNotifSwitches()
        const message =
          error?.response?.data?.error ||
          "Failed to update SMS notification settings"
        toast.error(<ToastInfo>{message}</ToastInfo>, {
          position: toast.POSITION.TOP_RIGHT,
        })
      })
  }

  const handleUpdatePhone = (newPhone) => {
    if (!newPhone || newPhone.trim() === "") {
      setEditingPhone(false)
      return
    }
    updateSettings({ phone_number: newPhone.trim() })
      .then(() => {
        setEditingPhone(false)
      })
      .catch(() => {
        setEditingPhone(false)
        showToast("Failed to update phone number")
      })
  }

  const showToast = (message) => {
    toast.info(<ToastInfo>{message}</ToastInfo>, {
      position: toast.POSITION.TOP_RIGHT,
    })
  }

  const completeMfaChallengeSuccess = (responseData, confirmGen) => {
    if (confirmGen !== mfaConfirmGenRef.current) return

    const enabled = responseData?.mfa_enabled === true
    setMfaEnabled(enabled)
    const hasNewRecoveryCodes =
      enabled && (responseData?.recovery_codes || []).length > 0
    toast.success(
      <ToastInfo>
        {enabled
          ? hasNewRecoveryCodes
            ? "Email MFA enabled. View your recovery codes in Account Settings when ready."
            : "Email MFA enabled."
          : "Email MFA disabled."}
      </ToastInfo>,
      { position: toast.POSITION.TOP_RIGHT },
    )
    if (refreshSettings) refreshSettings()
    cancelMfaChallenge()
  }

  const showMfaConfirmError = (error, confirmGen) => {
    if (confirmGen !== mfaConfirmGenRef.current) return

    if (isRateLimitError(error)) {
      showMfaRateLimitToast(error)
      setMfaError(getMfaRateLimitMessage(error))
    } else {
      setMfaError(
        error?.response?.data?.error ||
          "Invalid or expired verification code.",
      )
    }
    setMfaBusy(false)
    setMfaSwitchKey((key) => key + 1)
  }

  const reconcileMfaConfirmFailure = (error, confirmGen, mode) => {
    const challenge = mfaChallengeRef.current
    if (!challenge) return

    services.accountServices
      .getAccountSettings()
      .then(({ data }) => {
        if (!mfaChallengeRef.current) return

        const serverEnabled = data.mfa_enabled === true
        const intendedEnabled = mode === "enable"
        if (serverEnabled === intendedEnabled) {
          completeMfaChallengeSuccess(data, confirmGen)
          return
        }

        showMfaConfirmError(error, confirmGen)
      })
      .catch(() => showMfaConfirmError(error, confirmGen))
  }

  const cancelMfaChallenge = () => {
    mfaConfirmGenRef.current += 1
    setMfaChallenge(null)
    setMfaOtp("")
    setMfaError("")
    setMfaBusy(false)
    setSmsBusy(false)
    setSmsMessage("")
    setMfaSwitchKey((key) => key + 1)
  }

  const handleSendMfaSms = () => {
    if (!mfaChallenge?.mfa_token || mfaBusy || smsBusy) return

    setSmsBusy(true)
    setSmsMessage("Sending a code to your mobile number...")
    services.userServices
      .resendMfaSms(mfaChallenge.mfa_token)
      .then((res) => {
        if (res.data?.mfa_token) {
          setMfaChallenge((prev) =>
            prev ? { ...prev, mfa_token: res.data.mfa_token } : prev,
          )
        }
        const message = "Code sent. Check your phone for the text message."
        setSmsMessage(message)
        toast.success(<ToastInfo>{message}</ToastInfo>, {
          position: toast.POSITION.TOP_RIGHT,
        })
      })
      .catch((error) => {
        if (isRateLimitError(error)) {
          showMfaRateLimitToast(error)
          setSmsMessage(getMfaRateLimitMessage(error))
        } else {
          const message =
            getMfaErrorMessage(error) ||
            "Unable to send a text code. Please try again later."
          setSmsMessage(message)
          toast.error(<ToastInfo>{message}</ToastInfo>, {
            position: toast.POSITION.TOP_RIGHT,
          })
        }
      })
      .finally(() => setSmsBusy(false))
  }

  const handleToggleMfa = () => {
    if (mfaToggleInFlightRef.current || mfaBusy || mfaChallenge) return

    // Undo Switch optimistic toggle until OTP flow completes.
    setMfaSwitchKey((key) => key + 1)
    mfaToggleInFlightRef.current = true
    setMfaBusy(true)
    setMfaError("")
    const startingEnable = !mfaEnabled
    const request = startingEnable
      ? services.userServices.enableMfa()
      : services.userServices.disableMfa()

    request
      .then((res) => {
        setMfaChallenge({
          mode: startingEnable ? "enable" : "disable",
          mfa_token: res.data.mfa_token,
        })
        toast.success(
          <ToastInfo>A verification code has been sent to your email.</ToastInfo>,
          { position: toast.POSITION.TOP_RIGHT },
        )
      })
      .catch((error) => {
        setMfaSwitchKey((key) => key + 1)
        if (isRateLimitError(error)) {
          showMfaRateLimitToast(error)
        } else {
          toast.error(
            <ToastInfo>
              {error?.response?.data?.error || "Unable to update Email MFA."}
            </ToastInfo>,
            { position: toast.POSITION.TOP_RIGHT },
          )
        }
      })
      .finally(() => {
        mfaToggleInFlightRef.current = false
        setMfaBusy(false)
      })
  }

  const handleConfirmMfa = () => {
    if (!mfaChallenge || !/^\d{6}$/.test(mfaOtp)) {
      setMfaError("Please enter a valid 6-digit code")
      return
    }
    if (mfaConfirmInFlightRef.current || mfaBusy) return

    const confirmGen = mfaConfirmGenRef.current
    const mode = mfaChallenge.mode
    mfaConfirmInFlightRef.current = true
    setMfaBusy(true)
    setMfaError("")
    const confirm =
      mode === "enable"
        ? services.userServices.confirmEnableMfa
        : services.userServices.confirmDisableMfa

    confirm({ mfa_token: mfaChallenge.mfa_token, otp: mfaOtp })
      .then((res) => completeMfaChallengeSuccess(res.data, confirmGen))
      .catch((error) => reconcileMfaConfirmFailure(error, confirmGen, mode))
      .finally(() => {
        mfaConfirmInFlightRef.current = false
      })
  }

  const hideDetail = ({ detail, type }) => {
    let res = null

    if (detail) {
      if (type === "email") {
        const emailArray = detail.split("@")
        const partToHide = emailArray[0].slice(1, emailArray[0].length - 2)
        res = detail.replace(partToHide, "******")
      } else if (type === "phone_number") {
        const partToHide = detail.slice(1, detail.length - 2)
        res = detail.replace(partToHide, "**********")
      }
    }

    return res && <div className={styles.settingsValue}>{res}</div>
  }

  return (
    <div className={styles.settingsWrap}>
      <h1 className={styles.settingsTitle}>
        Account Settings Notifications
      </h1>

      <div className={styles.settingsContent}>
        <div className={styles.settingsTable}>
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Allow notifications</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                key={`allow-${allowNotifications}-${notifSwitchKey}`}
                defaultActive={allowNotifications}
                action={handleToggleAllowNotifications}
                switchStyle="big"
              />
            </div>
          </div>

          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Email</div>
            </div>
            <div className={styles.settingsTableCol}>
              {hideDetail({ detail: email, type: "email" })}
            </div>
          </div>

          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Send to email</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                key={`email-${sendToEmail}-${notifSwitchKey}`}
                defaultActive={sendToEmail}
                action={handleToggleSendToEmail}
                switchStyle="big"
                disabled={!allowNotifications}
              />
            </div>
          </div>

          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Mobile</div>
            </div>
            <div className={styles.settingsTableCol}>
              {editingPhone ? (
                <PhoneEditForm
                  initialPhone={phoneNumber}
                  onSave={handleUpdatePhone}
                  onCancel={() => setEditingPhone(false)}
                />
              ) : (
                <>
                  {phoneNumber ? (
                    <>
                      {hideDetail({ detail: phoneNumber, type: "phone_number" })}
                      <div
                        className={styles.settingsEditBtn}
                        onClick={() => setEditingPhone(true)}
                      >
                        <EditIcon />
                      </div>
                    </>
                  ) : (
                    <div
                      className={styles.settingsValue}
                      style={{ cursor: "pointer" }}
                      onClick={() => setEditingPhone(true)}
                    >
                      Add +
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Send to mobile</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                key={`sms-${sendToMobile}-${notifSwitchKey}`}
                defaultActive={sendToMobile}
                action={handleToggleSendToMobile}
                switchStyle="big"
                disabled={!allowNotifications}
              />
            </div>
          </div>

          <div
            className={classnames(
              styles.settingsTableRow,
              mfaChallenge && styles.mfaRowExpanded,
            )}
          >
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Email MFA</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                key={`${mfaEnabled}-${mfaSwitchKey}-${mfaChallenge ? "pending" : "idle"}`}
                defaultActive={mfaEnabled}
                action={handleToggleMfa}
                switchStyle="big"
                disabled={mfaBusy || !!mfaChallenge}
              />
            </div>
          </div>

          {mfaChallenge && (
            <div ref={mfaPanelRef}>
              <EmailMfaOtpChallenge
                variant="panel"
                title={
                  mfaChallenge.mode === "enable"
                    ? "Enable Email MFA"
                    : "Disable Email MFA"
                }
                otp={mfaOtp}
                onOtpChange={(value) => {
                  setMfaOtp(value)
                  if (mfaError) setMfaError("")
                }}
                error={mfaError}
                busy={mfaBusy}
                onCancel={cancelMfaChallenge}
                onConfirm={handleConfirmMfa}
                otpInputRef={mfaOtpInputRef}
                onSendSms={sendToMobile ? handleSendMfaSms : undefined}
                smsBusy={smsBusy}
                smsMessage={smsMessage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const PhoneEditForm = ({ initialPhone, onSave, onCancel }) => {
  const [phone, setPhone] = React.useState(initialPhone || "")
  const [error, setError] = React.useState("")

  const handleSubmit = (e) => {
    e.preventDefault()
    const trimmedPhone = phone.trim()
    if (!trimmedPhone) {
      onCancel()
      return
    }
    if (!E164_PHONE.test(trimmedPhone)) {
      setError("Phone number must be in E.164 format, e.g. +15551234567")
      return
    }
    setError("")
    onSave(trimmedPhone)
  }

  return (
    <form onSubmit={handleSubmit} className={styles.editForm}>
      <Input
        type="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value)
          if (error) setError("")
        }}
        placeholder="+15551234567"
        autoFocus
        error={!!error}
        errorMessage={error}
      />
      {error && (
        <p className={styles.phoneHelper}>
          Use international E.164 format starting with + and country code
          (example: +15551234567). No spaces or dashes.
        </p>
      )}
      <div className={styles.editFormButtons}>
        <button type="button" onClick={onCancel} className={styles.cancelBtn}>
          Cancel
        </button>
        <button type="submit" className={styles.saveBtn}>
          Save
        </button>
      </div>
    </form>
  )
}

export default NotificationsSettingsContainer

